/*
  Rebuy Smart Search — custom storefront integration.

  Powers two experiences from one element:
    <rebuy-search-element data-mode="drawer">  — instant search inside the top drawer
    <rebuy-search-element data-mode="page">    — full results page on /search

  Data comes from Rebuy's Smart Search API (search + quick-view endpoints).
  Card pricing mirrors snippets/product-card.liquid exactly (tier discount tags,
  "set of" badge, rounded percent-off, FREE SHIPPING rule) via a config JSON
  emitted by snippets/rebuy-search-config.liquid — no rules are duplicated here
  without a Liquid-injected source of truth.

  Add-to-cart goes through /cart/add.js with Rebuy attribution line-item
  properties; Rebuy's AJAX interception registers the add and the Smart Cart
  is shown (same pattern as snippets/combined-listing-cart-fix.liquid).
*/
(() => {
  'use strict';

  if (customElements.get('rebuy-search-element')) return;

  const ENDPOINT = 'https://rebuyengine.com/api/v1/smart-search';
  const RECENT_KEY = 'rbs:recent-searches';
  const RECENT_MAX = 6;

  /* ------------------------------------------------------------------ */
  /* Utilities                                                           */
  /* ------------------------------------------------------------------ */

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);

  const debounce = (fn, ms) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  // Shopify money format ("${{amount}}" etc.) applied to a cents value.
  function formatMoney(cents, format) {
    const rate = parseFloat(window.Shopify?.currency?.rate || 1) || 1;
    const value = (cents * rate) / 100;
    const withDelims = (v, decimals, thousands = ',', decimal = '.') => {
      const parts = v.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0);
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.');
        default: return withDelims(value, 2);
      }
    });
  }

  // Rebuy returns absolute myshopify URLs — keep navigation on the storefront domain.
  const productUrl = (p) => `${window.Shopify?.routes?.root || '/'}products/${p.handle}`;
  const collectionUrl = (c) => `${window.Shopify?.routes?.root || '/'}collections/${c.handle}`;

  function imageUrl(src, width) {
    if (!src) return '';
    try {
      const u = new URL(src);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (e) {
      return src;
    }
  }

  const handleize = (s) =>
    String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  function readRecent() {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter((s) => typeof s === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  function saveRecent(term) {
    term = (term || '').trim();
    if (term.length < 2) return;
    const list = readRecent().filter((s) => s.toLowerCase() !== term.toLowerCase());
    list.unshift(term);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* storage unavailable */ }
  }

  function clearRecent() {
    try { localStorage.removeItem(RECENT_KEY); } catch (e) { /* noop */ }
  }

  /* ------------------------------------------------------------------ */
  /* Pricing / labels — mirror of product-card.liquid                    */
  /* ------------------------------------------------------------------ */

  // Mirror of snippets/up-to-discount.liquid: the largest discount on the
  // product, but only when its purchasable variants are not all discounted by
  // the same amount. Returns 0 when there is no range to describe, so the
  // caller falls back to its usual single figure. Percentages are compared
  // after rounding — 34% and 36% both print 35%, so they are not a range.
  function upToPercent(variants) {
    let upTo = 0;
    let varies = false;
    let firstSeen = -1;

    // Sold-out variants count too (7 Oct 2026), matching up-to-discount.liquid.
    for (const v of variants) {

      const price = +v.price || 0;
      const compareAt = +v.compareAtPrice || 0;
      let pct = 0;
      if (compareAt > price) pct = Math.round(((compareAt - price) * 100) / compareAt / 5) * 5;

      if (pct > upTo) upTo = pct;
      if (firstSeen === -1) firstSeen = pct;
      else if (pct !== firstSeen) varies = true;
    }

    return varies ? upTo : 0;
  }

  // Mirror of snippets/landing-variant-on-sale.liquid: does the product have a
  // discounted variant a shopper can buy? Until 7 Oct 2026 this only asked about
  // the variant the search card opens on (the first purchasable one), which left
  // sets silent: they open on a full-price single piece and only the "Set of"
  // variant is discounted. The client asked for those badges back, so any
  // discounted variant now counts, sold-out ones included.
  //
  // Unknown rather than false when the payload carried no variants: with nothing
  // to read, the card keeps whatever it said before rather than losing a badge
  // to missing data.
  function landingVariantOnSale(variants) {
    if (!variants.length) return true;
    const discounted = (v) => (+v.compareAtPrice || 0) > (+v.price || 0);
    const landing = variants.find((v) => v.canPurchase !== false) || variants[0];
    return discounted(landing) || variants.some(discounted);
  }

  function cardData(p, cfg) {
    // Rebuy can return phantom variants (visible:false, price:0) on some
    // products — exclude them from pricing or "From $0.00" shows up.
    const allVariants = Array.isArray(p.variants) ? p.variants : [];
    let variants = allVariants.filter((v) => v.visible !== false && +v.price > 0);
    if (!variants.length) variants = allVariants;
    const prices = variants.length ? variants.map((v) => +v.price || 0) : [+p.price || 0];
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const available = p.canPurchase !== false;
    const tagsRaw = Array.isArray(p.tags) ? p.tags : [];
    const tags = tagsRaw.map((t) => String(t).trim().toLowerCase());

    // Compare-at price of the lowest-priced variant. Deliberately NOT the same
    // as Liquid's product.compare_at_price, which is the minimum over the
    // variants that have a compare-at at all — on a set that is the set's own
    // "was" price sitting next to a single piece's price, and comparing the two
    // yields 90% off where the truth is 15%. Liquid needs the SETS tag rule to
    // suppress that; taking the cheapest variant's own compare-at avoids it here.
    let cap = 0;
    if (variants.length) {
      const minVariant = variants.find((v) => (+v.price || 0) === min);
      cap = +((minVariant || {}).compareAtPrice) || 0;
    } else {
      cap = +p.compareAtPrice || 0;
    }

    // A product where every variant already has a compare-at price is not
    // eligible for the tag-driven tier discount (same rule as the theme).
    const allOnSale = variants.length > 0 && variants.every((v) => +v.compareAtPrice > 0);
    const eligible = available && !allOnSale;

    let factor = 0;
    let applied = false;
    if (eligible) {
      for (const tier of cfg.discountTiers || []) {
        if (tier.tag && tags.includes(tier.tag)) {
          factor = +tier.factor;
          applied = true;
          break;
        }
      }
    }

    let effective = min;
    let compare = 0;
    if (applied) {
      effective = Math.round(min * factor);
      compare = min;
    } else if (cap > min && available) {
      compare = cap;
    }

    const isSetsTagged = tags.includes('sets');
    if (isSetsTagged && !applied) compare = 0;

    // Percent-off ribbon — mirrors product-card-new-design.liquid: tier
    // discount first, compare-at otherwise; BUNDLES-tagged products only
    // show it when a tier discount applies. Rounded to the nearest 5 the same
    // way the product page rounds, so a search card and the page it links to
    // quote the same number — flooring the raw percentage first put them up to
    // 5 points apart.
    // A tier discount is exempt from the landing-variant gate: it comes off at
    // checkout rather than sitting on a compare-at price, so it applies to the
    // variant the shopper lands on too.
    let percentLabel = '';
    const isBundlesTagged = tags.includes('bundles');
    if (!(isBundlesTagged && !applied) && (applied || landingVariantOnSale(variants))) {
      // Asked before the cap > min fallback, matching the order in the Liquid
      // cards: on a set the cheapest variant is full price, so cap is 0 and the
      // fallback stays silent — but its dearer variants can still be discounted,
      // which is the case "Up to N% off" exists to describe.
      const upTo = applied ? 0 : upToPercent(variants);
      if (upTo > 0) {
        percentLabel = `Up to ${upTo}% off`;
      } else {
        let raw = 0;
        if (applied) {
          raw = ((min - effective) * 100) / min;
        } else if (cap > min) {
          raw = ((cap - min) * 100) / cap;
        }
        const rounded = Math.round(raw / 5) * 5;
        if (rounded > 0) percentLabel = `${rounded}% off`;
      }
    }

    // Image labels (out of stock / featured tag).
    let displayLabel = null;
    if (!available) displayLabel = { type: 'out-of-stock', text: cfg.t.outOfStock };

    // Featured label priority per product-card-new-design.liquid:
    // J. Bennett crest first, then Alice Lane exclusive, then any other
    // configured featured tag as plain text.
    let featuredLabel = null;
    if (available && (cfg.featuredTags || []).length) {
      const tagHandles = tagsRaw.map((t) => handleize(t));
      if (tagHandles.includes('j-bennett-collection')) {
        featuredLabel = {
          type: 'j-bennett-collection',
          crest: 'https://cdn.shopify.com/s/files/1/1798/1217/files/j-bennet-collection-product.png?v=1755003551',
          text: 'J. Bennett Design'
        };
      } else if (tagHandles.includes('exclusive')) {
        featuredLabel = {
          type: 'exclusive',
          crest: 'https://cdn.shopify.com/s/files/1/1798/1217/files/ALHC-Favicon-192.png?v=1757096227',
          text: 'Alice Lane Exclusive'
        };
      } else {
        outer:
        for (const ft of cfg.featuredTags) {
          for (const tag of tagsRaw) {
            if (String(tag).replace(/\s+/g, '').toLowerCase() === ft) {
              if (handleize(tag) === 'sale') continue;
              featuredLabel = { type: handleize(tag), text: tag };
              break outer;
            }
          }
        }
      }
    }

    const freeShipping = !cfg.hideFreeShipping && cfg.freeShippingVendor &&
      p.vendor === cfg.freeShippingVendor;

    const singleVariant = variants.length === 1 &&
      String(variants[0].name || '').toLowerCase() === 'default title';

    return {
      available,
      priceVaries: prices.length > 1 && min !== max,
      effective,
      compare,
      percentLabel,
      displayLabel,
      featuredLabel,
      freeShipping,
      quickAddId: singleVariant && available && cfg.quickAdd ? variants[0].id : null
    };
  }

  // Card markup mirrors snippets/product-card-new-design.liquid (the
  // redesigned collection card), scaled by CSS for the search grids.
  // Mirror of snippets/set-landing-variant.liquid: a set product (tagged SETS)
  // opens on its "Set of" variant only when the card's photo is that variant's
  // photo, so the page opens on what the shopper clicked. Since 9 Oct 2026;
  // before that every set opened on its set, including single-item cards such
  // as Bubble Candle Dish. A sold-out set still counts; an in-stock one wins.
  // Null when the product is not a set, the photo belongs to no set variant,
  // or the variant the page opens on anyway already shows that photo.
  function setLandingVariantId(p) {
    const tags = (Array.isArray(p.tags) ? p.tags : []).map((t) => String(t).trim().toLowerCase());
    if (!tags.includes('sets')) return null;
    const path = (u) => String(u || '').split('?')[0];
    const photo = path(p.images && p.images[0] && p.images[0].url);
    if (!photo) return null;
    const photoOf = (v) => path(v.images && v.images[0] && v.images[0].url);
    const variants = Array.isArray(p.variants) ? p.variants.filter((v) => v.visible !== false) : [];
    const isSet = (v) => String(v.name || v.title || '').toLowerCase().includes('set of');
    const opening = variants.find((v) => v.canPurchase !== false) || variants[0];
    if (!opening || photoOf(opening) === photo) return null;
    const matches = (v) => isSet(v) && v.id && photoOf(v) === photo;
    const set = variants.find((v) => v.canPurchase !== false && matches(v)) || variants.find(matches);
    return set ? set.id : null;
  }

  function cardHTML(p, cfg, origin) {
    const d = cardData(p, cfg);
    const img = (p.images && p.images[0]) || null;
    const setVariantId = setLandingVariantId(p);
    const url = productUrl(p) + (setVariantId ? `?variant=${setVariantId}` : '');

    let labels = '';
    if (d.displayLabel || d.featuredLabel) {
      const one = (l, cls) => {
        if (!l) return '';
        const content = l.crest
          ? `<img src="${l.crest}" alt="" width="34" height="41"> ${esc(l.text)}`
          : esc(l.text);
        return `
        <div class="product--label ${cls}" data-background-color="${esc(cfg.labelColor)}" data-label-shape="${esc(cfg.labelShape)}" data-label-type="${esc(l.type)}">${content}</div>`;
      };
      labels = `
      <div class="product--labels" data-item="overline" data-text-color="${cfg.labelWhiteText ? 'white' : 'body'}">
        ${one(d.displayLabel, 'display')}${one(d.featuredLabel, 'featured')}
      </div>`;
    }

    const ribbon = d.percentLabel
      ? `<div class="percentOff"><p class="rawPercentOff">${esc(d.percentLabel)}</p></div>`
      : '';

    const image = img ? `
      <img class="product-card--image" loading="lazy" alt="${esc(img.alt || p.name)}"
        src="${esc(imageUrl(img.url, 500))}"
        srcset="${esc(imageUrl(img.url, 300))} 300w, ${esc(imageUrl(img.url, 500))} 500w, ${esc(imageUrl(img.url, 767))} 767w"
        sizes="(max-width: 767px) 50vw, 25vw">` : '';

    const quickAdd = d.quickAddId ? `
      <button class="rbs-quick-add" type="button" data-rbs-quick-add
        data-variant-id="${esc(d.quickAddId)}" data-origin="${esc(origin)}"
        aria-label="${esc(cfg.t.addToCart)}: ${esc(p.name)}">
        <span class="rbs-quick-add--label">${esc(cfg.t.addToCart)}</span>
        <span class="rbs-quick-add--plus" aria-hidden="true">+</span>
      </button>` : '';

    // "PENNY LAMP | Brass" renders as two lines, like the updated card.
    const title = esc(p.name).replace(/ \|/g, '<br>');

    return `
    <product-card class="product-card--root" data-handle="${esc(p.handle)}" data-product-item
      data-text-layout="${esc(cfg.textAlignment)}" data-container="block"
      data-aspect-ratio="${esc(cfg.aspectRatio)}">
      <a href="${esc(url)}" class="product-card--image-wrapper" data-rbs-nav data-rbs-product ${d.featuredLabel && d.featuredLabel.crest ? 'data-has-crest' : ''}>
        ${labels}${ribbon}${image}${quickAdd}
      </a>
      <div class="product-card--details">
        <div class="product-card--details-wrapper">
          <p class="product--title" data-item="${esc(cfg.headingType)}"><a href="${esc(url)}">${title}</a></p>
        </div>
        <div class="merged-price-shipping">
          <div class="product--price-container">
            <div class="product--price-wrapper">
              ${d.priceVaries ? `<span class="product--from" data-item="nav-text">${esc(cfg.t.from)}</span>` : ''}
              <span class="product--price" data-item="nav-text">${formatMoney(d.effective, cfg.moneyFormat)}</span>
              ${d.compare ? `<span class="product--compare-price" data-item="nav-text" data-text-color="gray">${formatMoney(d.compare, cfg.moneyFormat)}</span>` : ''}
            </div>
          </div>
          ${d.freeShipping ? `<p class="freeShipping">FREE SHIPPING</p>` : ''}
        </div>
      </div>
    </product-card>`;
  }

  // Rebuy's classic data API (top_sellers) returns Shopify REST-style
  // products — adapt them to the Smart Search shape cardHTML expects.
  function normalizeClassicProduct(p) {
    const cents = (v) => Math.round(parseFloat(v || 0) * 100) || 0;
    const variantAvailable = (v) =>
      v.inventory_management !== 'shopify' ||
      v.inventory_policy === 'continue' ||
      (parseInt(v.inventory_quantity, 10) || 0) > 0;
    const variants = (p.variants || []).map((v) => ({
      id: v.id,
      name: v.title || '',
      price: cents(v.price),
      compareAtPrice: cents(v.compare_at_price),
      canPurchase: variantAvailable(v),
      visible: true
    }));
    return {
      name: p.title,
      handle: p.handle,
      vendor: p.vendor,
      tags: typeof p.tags === 'string'
        ? p.tags.split(',').map((s) => s.trim()).filter(Boolean)
        : (p.tags || []),
      images: (p.images || []).map((i) => ({ url: i.src, alt: i.alt || '' })),
      price: variants.length ? Math.min(...variants.map((v) => v.price)) : 0,
      compareAtPrice: 0,
      canPurchase: variants.some((v) => v.canPurchase),
      variants
    };
  }

  const skeletonHTML = (n) => Array.from({ length: n }, () => `
    <div class="rbs-skeleton">
      <div class="rbs-skeleton--image"></div>
      <div class="rbs-skeleton--line"></div>
      <div class="rbs-skeleton--line rbs-skeleton--line-short"></div>
    </div>`).join('');

  /* ------------------------------------------------------------------ */
  /* API                                                                 */
  /* ------------------------------------------------------------------ */

  class RebuyAPI {
    constructor(cfg) {
      this.cfg = cfg;
      this.cache = new Map();
    }

    get key() {
      // Prefer the theme setting; fall back to the key the Rebuy embed exposes.
      return this.cfg.apiKey || window.Rebuy?.shop?.api_key || window.rebuyConfig?.shop?.api_key || '';
    }

    async post(path, body, signal, cacheable = true) {
      const cacheKey = cacheable ? path + JSON.stringify(body) : null;
      if (cacheKey && this.cache.has(cacheKey)) return this.cache.get(cacheKey);

      const res = await fetch(ENDPOINT + path, {
        method: 'POST',
        signal,
        headers: {
          'Content-Type': 'application/json',
          'Rebuy-Api-Key': this.key
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(`Rebuy Smart Search ${path} failed: ${res.status}`);
      const json = await res.json();
      if (!json.successful) throw new Error(json.errorMessage || 'Rebuy Smart Search error');
      if (cacheKey) {
        this.cache.set(cacheKey, json.data);
        if (this.cache.size > 60) this.cache.delete(this.cache.keys().next().value);
      }
      return json.data;
    }

    search(term, options = {}, signal) {
      return this.post('/search', {
        searchTerm: term,
        pageSize: options.pageSize || 8,
        currentPage: options.page || 1,
        searchContent: options.content || false,
        ...(options.sortBy ? { sortBy: options.sortBy } : {}),
        ...(options.filters ? { filters: options.filters } : {}),
        ...(options.aggregate ? { aggregate: options.aggregate } : {})
      }, signal);
    }

    quickView(term, signal) {
      return this.post('/search/quick-view', { query: term }, signal);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Add to cart (Rebuy Smart Cart aware)                                */
  /* ------------------------------------------------------------------ */

  async function addToCart(variantId, origin) {
    const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            _source: 'Rebuy',
            _attribution: 'Rebuy Smart Search',
            _search_item_origin: origin || 'Quick View'
          }
        }]
      })
    });
    if (!res.ok) throw new Error('Add to cart failed: ' + res.status);
    const R = window.Rebuy || window.rebuy;
    if (R && R.SmartCart && typeof R.SmartCart.show === 'function') {
      R.SmartCart.show();
    } else {
      window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Custom element                                                      */
  /* ------------------------------------------------------------------ */

  class RebuySearch extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;

      const cfgEl = this.querySelector('[data-rebuy-search-config]');
      this.cfg = cfgEl ? JSON.parse(cfgEl.textContent) : {};
      this.cfg.t = this.cfg.t || {};
      this.api = new RebuyAPI(this.cfg);
      this.mode = this.dataset.mode || 'drawer';

      this.input = this.querySelector('[data-rbs-input]');
      this.ghost = this.querySelector('[data-rbs-ghost]');
      this.clearBtn = this.querySelector('[data-rbs-clear]');
      this.emptyState = this.querySelector('[data-rbs-empty-state]');
      this.resultsEl = this.querySelector('[data-rbs-results]');
      this.form = this.querySelector('form');

      this.abort = null;
      this.lastTerm = '';
      this.completion = '';
      this.navIndex = -1;

      this.bindCommon();
      if (this.mode === 'page') this.initPage();
      else this.initDrawer();
    }

    /* ---------------- shared behaviour ---------------- */

    bindCommon() {
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.submit(this.input.value);
        });
      }

      if (this.input) {
        this.input.addEventListener('input', () => {
          this.toggleClear();
          this.onInput(this.input.value);
        });
        this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      }

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.input.value = '';
          this.input.focus();
          this.toggleClear();
          this.onInput('');
        });
      }

      // Delegated clicks: quick add, trending/recent terms, suggestions.
      this.addEventListener('click', (e) => {
        const qa = e.target.closest('[data-rbs-quick-add]');
        if (qa) {
          e.preventDefault();
          e.stopPropagation();
          this.quickAdd(qa);
          return;
        }
        const term = e.target.closest('[data-rbs-term]');
        if (term) {
          e.preventDefault();
          const value = term.getAttribute('data-rbs-term');
          if (term.hasAttribute('data-rbs-submit')) {
            this.submit(value);
          } else {
            this.input.value = value;
            this.input.focus();
            this.toggleClear();
            this.onInput(value, true);
          }
          return;
        }
        const clear = e.target.closest('[data-rbs-clear-recent]');
        if (clear) {
          e.preventDefault();
          clearRecent();
          this.renderRecent();
          return;
        }
        const product = e.target.closest('[data-rbs-product]');
        if (product) saveRecent(this.input ? this.input.value : '');
      });
    }

    toggleClear() {
      if (this.clearBtn) this.clearBtn.style.display = this.input.value ? '' : 'none';
    }

    setLoading(on) {
      this.setAttribute('data-loading', on ? 'true' : 'false');
    }

    submit(term) {
      term = (term || '').trim();
      if (!term) return;
      saveRecent(term);
      if (this.mode === 'page') {
        this.state.q = term;
        this.state.page = 1;
        this.input.value = term;
        this.toggleClear();
        this.runPageSearch();
      } else {
        window.location.href = `${this.cfg.searchUrl || '/search'}?q=${encodeURIComponent(term)}`;
      }
    }

    async quickAdd(btn) {
      if (btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      try {
        await addToCart(btn.dataset.variantId, btn.dataset.origin);
        saveRecent(this.input ? this.input.value : '');
      } catch (err) {
        const link = btn.closest('product-card')?.querySelector('.product-card--image-wrapper');
        if (link) window.location.href = link.href;
      } finally {
        btn.setAttribute('aria-busy', 'false');
      }
    }

    /* ---------------- ghost autofill ---------------- */

    // Complete the last typed word from the most frequent matching word in
    // result names/categories: "lam" -> "lamp".
    computeCompletion(term, products, extras) {
      this.completion = '';
      this.setAttribute('data-completion', 'false');
      if (this.ghost) this.ghost.textContent = '';
      const trimmed = term.trimStart();
      if (!trimmed || /\s$/.test(term)) return;

      const words = trimmed.toLowerCase().split(/\s+/);
      const last = words[words.length - 1];
      if (!last || last.length < 2) return;

      const counts = new Map();
      const feed = (text, weight = 1) => {
        String(text || '').toLowerCase().split(/[^a-z0-9'-]+/).forEach((w) => {
          if (w.length > last.length && w.startsWith(last)) {
            counts.set(w, (counts.get(w) || 0) + weight);
          }
        });
      };
      (products || []).forEach((p) => {
        feed(p.name, 2);
        (p.categories || []).forEach((c) => feed(c, 3));
      });
      (extras || []).forEach((x) => feed(x, 3));

      let best = '';
      let bestScore = 0;
      counts.forEach((score, w) => {
        if (score > bestScore || (score === bestScore && w.length < best.length)) {
          best = w;
          bestScore = score;
        }
      });
      if (!best) return;

      this.completion = best.slice(last.length);
      this.setAttribute('data-completion', 'true');
      if (this.ghost) {
        this.ghost.innerHTML = `<i>${esc(this.input.value)}</i>${esc(this.completion)}`;
      }
    }

    acceptCompletion() {
      if (!this.completion) return false;
      this.input.value += this.completion;
      this.completion = '';
      this.setAttribute('data-completion', 'false');
      if (this.ghost) this.ghost.textContent = '';
      this.toggleClear();
      this.onInput(this.input.value, true);
      return true;
    }

    /* ---------------- keyboard navigation ---------------- */

    navItems() {
      return [...this.querySelectorAll('[data-rbs-nav]')].filter((el) => el.offsetParent !== null);
    }

    onKeydown(e) {
      if (e.key === 'Tab' && this.completion) {
        e.preventDefault();
        this.acceptCompletion();
        return;
      }
      if (e.key === 'ArrowRight' && this.completion &&
          this.input.selectionStart === this.input.value.length) {
        e.preventDefault();
        this.acceptCompletion();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const items = this.navItems();
        if (!items.length) return;
        e.preventDefault();
        this.navIndex += e.key === 'ArrowDown' ? 1 : -1;
        if (this.navIndex < 0) this.navIndex = items.length - 1;
        if (this.navIndex >= items.length) this.navIndex = 0;
        items.forEach((el, i) => el.classList.toggle('rbs-nav-active', i === this.navIndex));
        items[this.navIndex].scrollIntoView({ block: 'nearest' });
        return;
      }
      if (e.key === 'Enter') {
        const active = this.querySelector('.rbs-nav-active');
        if (active) {
          e.preventDefault();
          // Terms search inline (their click handler prevents navigation);
          // links save the search, then click() navigates natively.
          if (active.href && !active.hasAttribute('data-rbs-term')) saveRecent(this.input.value);
          active.click();
        }
      }
    }

    resetNav() {
      this.navIndex = -1;
      this.querySelectorAll('.rbs-nav-active').forEach((el) => el.classList.remove('rbs-nav-active'));
    }

    /* ---------------- featured products (Rebuy top sellers) ---------------- */

    async loadFeatured() {
      if (this.__featuredLoaded) return;
      this.__featuredLoaded = true;
      const holders = [...this.querySelectorAll('[data-rbs-featured]')];
      if (!holders.length) return;
      try {
        const key = this.api.key;
        if (!key) return;
        const limit = 4;
        // A merchant-configured Rebuy custom data source (e.g. ranked by
        // revenue) takes priority; Rebuy Top Sellers is the default.
        const path = this.cfg.featuredSourceId
          ? `/api/v1/custom/id/${encodeURIComponent(this.cfg.featuredSourceId)}`
          : '/api/v1/products/top_sellers';
        const res = await fetch(`https://rebuyengine.com${path}?key=${encodeURIComponent(key)}&limit=${limit}`);
        if (!res.ok) return;
        const json = await res.json();
        const products = (json.data || [])
          .map(normalizeClassicProduct)
          .filter((p) => p.name && p.images.length && p.canPurchase);
        if (!products.length) return;
        holders.forEach((h) => {
          h.innerHTML = products.map((p) => cardHTML(p, this.cfg, 'Suggested')).join('');
        });
      } catch (e) {
        /* keep the server-rendered fallback cards */
      }
    }

    /* ================= DRAWER MODE ================= */

    initDrawer() {
      this.debouncedSearch = debounce((term) => this.drawerSearch(term), 200);
      this.renderRecent();
      this.toggleClear();
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.loadFeatured(), { timeout: 3000 });
      } else {
        setTimeout(() => this.loadFeatured(), 1200);
      }
    }

    onInput(value, immediate = false) {
      const term = value.trim();
      this.resetNav();

      if (this.mode === 'page') {
        // On the results page the input only searches on submit,
        // but we still surface ghost completion from the last result set.
        return;
      }

      if (!term) {
        this.abort?.abort();
        this.completion = '';
        this.setAttribute('data-completion', 'false');
        if (this.ghost) this.ghost.textContent = '';
        this.showEmptyState(true);
        this.setLoading(false);
        return;
      }

      this.showEmptyState(false);
      if (immediate) this.drawerSearch(term);
      else this.debouncedSearch(term);
    }

    showEmptyState(show) {
      if (this.emptyState) this.emptyState.style.display = show ? '' : 'none';
      if (this.resultsEl) this.resultsEl.style.display = show ? 'none' : '';
      if (show && this.resultsEl) this.resultsEl.innerHTML = '';
      if (show) this.renderRecent();
    }

    renderRecent() {
      const holder = this.querySelector('[data-rbs-recent]');
      if (!holder) return;
      const recent = readRecent();
      if (!recent.length) {
        holder.style.display = 'none';
        holder.innerHTML = '';
        return;
      }
      holder.style.display = '';
      holder.innerHTML = `
        <div class="rbs-block-heading" data-item="block-heading">
          ${esc(this.cfg.t.recent)}
          <button type="button" class="rbs-recent-clear" data-rbs-clear-recent aria-label="${esc(this.cfg.t.clear)}">${esc(this.cfg.t.clear)}</button>
        </div>
        <div class="rbs-chips">
          ${recent.map((r) => `
            <button type="button" class="rbs-chip" data-rbs-term="${esc(r)}">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
              ${esc(r)}
            </button>`).join('')}
        </div>`;
    }

    async drawerSearch(term) {
      this.abort?.abort();
      const controller = new AbortController();
      this.abort = controller;
      this.setLoading(true);

      if (this.resultsEl && !this.resultsEl.querySelector('.rbs-grid')) {
        this.resultsEl.innerHTML = `
          <div class="rbs-results-layout">
            <div class="rbs-aside"></div>
            <div class="rbs-main"><div class="rbs-grid" data-rbs-grid>${skeletonHTML(this.cfg.drawerProducts || 8)}</div></div>
          </div>`;
      }

      try {
        const search = await this.api.search(term, {
          pageSize: this.cfg.drawerProducts || 8,
          content: true,
          aggregate: { fields: ['categories', 'vendors'] }
        }, controller.signal);
        if (controller.signal.aborted) return;

        this.lastTerm = term;
        this.renderDrawerResults(term, search);
        this.dispatchEvent(new CustomEvent('rebuy-search:results', {
          bubbles: true,
          detail: { term, total: search.pageInfo?.serverSize || 0 }
        }));
      } catch (err) {
        if (controller.signal.aborted) return;
        this.renderDrawerError(term);
      } finally {
        if (!controller.signal.aborted) this.setLoading(false);
      }
    }

    // Query suggestions: completion + matching categories/vendors, e.g.
    // "lamp" -> table lamps, floor lamps, Visual Comfort.
    buildSuggestions(term, search) {
      const out = [];
      const seen = new Set();
      const push = (label, submit = false) => {
        const k = label.toLowerCase();
        if (!k || seen.has(k) || k === term.toLowerCase()) return;
        seen.add(k);
        out.push({ label, submit });
      };

      const aggs = {};
      (search.aggregates || []).forEach((a) => Object.assign(aggs, a));
      const cats = (aggs.categories || []).map((c) => String(c.categories || '').trim()).filter(Boolean);
      const words = term.toLowerCase().split(/\s+/).filter(Boolean);

      if (this.completion) {
        const base = term.replace(/[^\s]+$/, '');
        const lastWord = term.slice(base.length);
        push(base + lastWord + this.completion);
      }
      cats.slice(0, 5).forEach((c) => {
        const lc = c.toLowerCase();
        if (words.some((w) => lc.includes(w))) push(lc);
      });
      const vendors = (aggs.vendorAsTag || []).map((v) => String(v.vendorAsTag || '').trim()).filter(Boolean);
      vendors.slice(0, 3).forEach((v) => {
        const lv = v.toLowerCase();
        if (words.some((w) => lv.includes(w))) push(v);
      });
      return out.slice(0, 6);
    }

    highlight(label, term) {
      const words = term.toLowerCase().split(/\s+/).filter((w) => w.length > 1);
      let html = esc(label);
      words.forEach((w) => {
        html = html.replace(new RegExp(`(${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'), '<strong>$1</strong>');
      });
      return html;
    }

    renderDrawerResults(term, search) {
      const products = search.products || [];
      const total = search.pageInfo?.serverSize || products.length;
      const extras = [];
      (search.aggregates || []).forEach((a) => {
        (a.categories || []).forEach((c) => extras.push(c.categories));
      });
      this.computeCompletion(this.input.value, products, extras);

      const suggestions = this.buildSuggestions(term, search);
      const content = (search.content || []).slice(0, 3);

      const asideBlocks = [];
      if (suggestions.length) {
        asideBlocks.push(`
          <div class="rbs-block">
            <div class="rbs-block-heading" data-item="block-heading">${esc(this.cfg.t.suggestions)}</div>
            <ul class="rbs-list">
              ${suggestions.map((s) => `
                <li><a href="${esc((this.cfg.searchUrl || '/search') + '?q=' + encodeURIComponent(s.label))}"
                       data-rbs-term="${esc(s.label)}" data-rbs-nav>${this.highlight(s.label, term)}</a></li>`).join('')}
            </ul>
          </div>`);
      }
      if (content.length) {
        asideBlocks.push(`
          <div class="rbs-block">
            <div class="rbs-block-heading" data-item="block-heading">${esc(this.cfg.t.blog)}</div>
            <ul class="rbs-list">
              ${content.map((a) => `
                <li><a href="${esc((window.Shopify?.routes?.root || '/') + 'blogs/' + (a.blogHandle || handleize(a.blogTitle) || 'news') + '/' + a.handle)}" data-rbs-nav>${esc(a.title)}</a></li>`).join('')}
            </ul>
          </div>`);
      }

      const grid = products.length
        ? products.map((p) => cardHTML(p, this.cfg, 'Quick View')).join('')
        : '';

      const viewAllUrl = `${this.cfg.searchUrl || '/search'}?q=${encodeURIComponent(term)}`;

      this.resultsEl.innerHTML = `
        <div class="rbs-results-layout" ${asideBlocks.length ? '' : 'data-no-aside'}>
          ${asideBlocks.length ? `<div class="rbs-aside">${asideBlocks.join('')}</div>` : ''}
          <div class="rbs-main">
            ${products.length ? `
              <div class="rbs-block-heading rbs-products-heading" data-item="block-heading">
                ${esc(this.cfg.t.products)}
                <span class="rbs-count">${total} ${esc(total === 1 ? this.cfg.t.result : this.cfg.t.results)}</span>
              </div>
              <div class="rbs-grid" data-rbs-grid>${grid}</div>
              <div class="rbs-view-all">
                <a href="${esc(viewAllUrl)}" data-item="button" data-rbs-view-all data-transition-button>
                  <span>${esc(this.cfg.t.viewAll)} (${total}) &rarr;</span>
                </a>
              </div>`
            : `
              <div class="rbs-no-results">
                <p data-item="paragraph">${esc(this.cfg.t.noResults)} &ldquo;${esc(term)}&rdquo;</p>
              </div>`}
          </div>
        </div>`;

      // Persist the search once a shopper commits to "view all".
      this.resultsEl.querySelector('[data-rbs-view-all]')?.addEventListener('click', () => saveRecent(term));
    }

    renderDrawerError(term) {
      if (!this.resultsEl) return;
      this.resultsEl.innerHTML = `
        <div class="rbs-no-results">
          <p data-item="paragraph">${esc(this.cfg.t.error)}</p>
          <a href="${esc((this.cfg.searchUrl || '/search') + '?q=' + encodeURIComponent(term))}" data-item="block-link"><div>${esc(this.cfg.t.viewAll)} &rarr;</div></a>
        </div>`;
    }

    /* ================= PAGE MODE ================= */

    initPage() {
      const params = new URLSearchParams(window.location.search);
      this.state = {
        q: (params.get('q') || '').replace(/\*+$/, ''),
        sort: params.get('sort') || 'relevance',
        page: 1,
        categories: params.getAll('category'),
        vendors: params.getAll('vendor'),
        min: params.get('min') ? parseFloat(params.get('min')) : null,
        max: params.get('max') ? parseFloat(params.get('max')) : null,
        inStock: params.get('stock') === '1'
      };
      this.products = [];
      this.aggregates = {};
      this.total = 0;

      if (this.input) {
        this.input.value = this.state.q;
        this.toggleClear();
      }

      this.sortEl = this.querySelector('[data-rbs-sort]');
      if (this.sortEl) {
        this.sortEl.value = this.state.sort;
        this.sortEl.addEventListener('change', () => {
          this.state.sort = this.sortEl.value;
          this.state.page = 1;
          this.runPageSearch();
        });
      }

      this.querySelector('[data-rbs-filters-open]')?.addEventListener('click', () => {
        this.querySelector('[data-rbs-filters]')?.setAttribute('data-open', 'true');
        document.documentElement.style.overflow = 'hidden';
      });
      this.querySelector('[data-rbs-filters-close]')?.addEventListener('click', () => this.closeFilters());
      this.querySelector('[data-rbs-filters-overlay]')?.addEventListener('click', () => this.closeFilters());

      this.addEventListener('change', (e) => {
        const f = e.target.closest('[data-rbs-filter]');
        if (f) this.onFilterChange(f);
      });
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-rbs-remove-filter]');
        if (chip) {
          e.preventDefault();
          this.removeFilter(chip.dataset.filterType, chip.dataset.filterValue);
        }
        if (e.target.closest('[data-rbs-clear-filters]')) {
          e.preventDefault();
          Object.assign(this.state, { categories: [], vendors: [], min: null, max: null, inStock: false, page: 1 });
          this.runPageSearch();
        }
        if (e.target.closest('[data-rbs-load-more]')) {
          e.preventDefault();
          this.state.page += 1;
          this.runPageSearch(true);
        }
      });

      const priceForm = this.querySelector('[data-rbs-price-form]');
      priceForm?.addEventListener('submit', (e) => {
        e.preventDefault();
        const min = parseFloat(priceForm.querySelector('[name="min"]').value);
        const max = parseFloat(priceForm.querySelector('[name="max"]').value);
        this.state.min = isNaN(min) ? null : min;
        this.state.max = isNaN(max) ? null : max;
        this.state.page = 1;
        this.runPageSearch();
      });

      this.runPageSearch();
    }

    closeFilters() {
      this.querySelector('[data-rbs-filters]')?.setAttribute('data-open', 'false');
      document.documentElement.style.overflow = '';
    }

    onFilterChange(el) {
      const type = el.dataset.filterType;
      const value = el.value;
      this.state.page = 1;
      if (type === 'stock') {
        this.state.inStock = el.checked;
      } else {
        const list = this.state[type] || [];
        if (el.checked) {
          if (!list.includes(value)) list.push(value);
        } else {
          this.state[type] = list.filter((v) => v !== value);
        }
      }
      this.runPageSearch();
    }

    removeFilter(type, value) {
      this.state.page = 1;
      if (type === 'stock') this.state.inStock = false;
      else if (type === 'price') { this.state.min = null; this.state.max = null; }
      else this.state[type] = (this.state[type] || []).filter((v) => v !== value);
      this.runPageSearch();
    }

    buildFilters() {
      const filters = {};
      if (this.state.categories.length) filters.categories = this.state.categories;
      if (this.state.vendors.length) filters.vendors = this.state.vendors;
      if (this.state.inStock) filters.available = true;
      if (this.state.min != null || this.state.max != null) {
        filters.price = {
          min: Math.round((this.state.min ?? 0) * 100),
          max: Math.round((this.state.max ?? 1000000) * 100)
        };
      }
      return Object.keys(filters).length ? filters : null;
    }

    sortMap() {
      switch (this.state.sort) {
        case 'price-asc': return { field: 'price', order: 'ASC' };
        case 'price-desc': return { field: 'price', order: 'DESC' };
        case 'newest': return { field: 'created_at', order: 'DESC' };
        case 'best-sellers': return { field: 'best-sellers', order: 'DESC' };
        default: return null;
      }
    }

    syncURL() {
      const params = new URLSearchParams();
      if (this.state.q) params.set('q', this.state.q);
      if (this.state.sort !== 'relevance') params.set('sort', this.state.sort);
      this.state.categories.forEach((c) => params.append('category', c));
      this.state.vendors.forEach((v) => params.append('vendor', v));
      if (this.state.min != null) params.set('min', this.state.min);
      if (this.state.max != null) params.set('max', this.state.max);
      if (this.state.inStock) params.set('stock', '1');
      const qs = params.toString();
      history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
    }

    async runPageSearch(append = false) {
      this.abort?.abort();
      const controller = new AbortController();
      this.abort = controller;
      this.setLoading(true);
      this.syncURL();

      const grid = this.querySelector('[data-rbs-grid]');
      if (!append && grid) grid.innerHTML = skeletonHTML(this.cfg.pageSize || 24);

      try {
        const data = await this.api.search(this.state.q, {
          pageSize: this.cfg.pageSize || 24,
          page: this.state.page,
          content: false,
          sortBy: this.sortMap(),
          filters: this.buildFilters(),
          aggregate: { fields: ['categories', 'vendors', 'availability', 'price'] }
        }, controller.signal);
        if (controller.signal.aborted) return;

        const products = data.products || [];
        this.total = data.pageInfo?.serverSize ?? products.length;
        this.products = append ? this.products.concat(products) : products;
        (data.aggregates || []).forEach((a) => Object.assign(this.aggregates, a));

        // The API sorts on the pre-discount base price; re-sort what we show
        // by the effective (tier-discounted, lowest-variant) price so the
        // visible order matches the prices on the cards.
        if (this.state.sort === 'price-asc' || this.state.sort === 'price-desc') {
          const dir = this.state.sort === 'price-asc' ? 1 : -1;
          this.products = this.products
            .map((p) => ({ p, key: cardData(p, this.cfg).effective }))
            .sort((a, b) => (a.key - b.key) * dir)
            .map((x) => x.p);
        }

        this.renderPage(append);
        this.dispatchEvent(new CustomEvent('rebuy-search:results', {
          bubbles: true,
          detail: { term: this.state.q, total: this.total, page: this.state.page }
        }));
      } catch (err) {
        if (!controller.signal.aborted) this.renderPageError();
      } finally {
        if (!controller.signal.aborted) this.setLoading(false);
      }
    }

    activeFilterChips() {
      const chips = [];
      this.state.categories.forEach((c) => chips.push({ type: 'categories', value: c, label: c.toLowerCase() }));
      this.state.vendors.forEach((v) => chips.push({ type: 'vendors', value: v, label: v }));
      if (this.state.min != null || this.state.max != null) {
        chips.push({
          type: 'price', value: '',
          label: `${formatMoney((this.state.min ?? 0) * 100, this.cfg.moneyFormat)} – ${this.state.max != null ? formatMoney(this.state.max * 100, this.cfg.moneyFormat) : '∞'}`
        });
      }
      if (this.state.inStock) chips.push({ type: 'stock', value: '', label: this.cfg.t.inStock });
      return chips;
    }

    renderPage(append) {
      const headline = this.querySelector('[data-rbs-headline]');
      if (headline) {
        headline.innerHTML = this.state.q
          ? `${esc(this.cfg.t.showingFor)} &ldquo;<strong>${esc(this.state.q)}</strong>&rdquo; <span class="rbs-count">(${this.total})</span>`
          : `${esc(this.cfg.t.allProducts)} <span class="rbs-count">(${this.total})</span>`;
      }

      const chipsEl = this.querySelector('[data-rbs-active-filters]');
      if (chipsEl) {
        const chips = this.activeFilterChips();
        chipsEl.innerHTML = chips.length ? `
          ${chips.map((c) => `
            <button type="button" class="rbs-chip rbs-chip--active" data-rbs-remove-filter
              data-filter-type="${esc(c.type)}" data-filter-value="${esc(c.value)}">
              ${esc(c.label)} <span aria-hidden="true">&times;</span>
            </button>`).join('')}
          <button type="button" class="rbs-chip rbs-chip--clear" data-rbs-clear-filters>${esc(this.cfg.t.clearAll)}</button>` : '';
      }

      this.renderFacets();

      const grid = this.querySelector('[data-rbs-grid]');
      if (grid) {
        const cards = this.products.map((p) => cardHTML(p, this.cfg, 'Results Page')).join('');
        if (this.products.length) {
          grid.innerHTML = cards;
        } else {
          grid.innerHTML = '';
        }
      }

      const empty = this.querySelector('[data-rbs-page-empty]');
      if (empty) empty.style.display = this.products.length ? 'none' : '';
      if (!this.products.length) this.loadFeatured();

      const more = this.querySelector('[data-rbs-more-wrap]');
      if (more) {
        const shown = this.products.length;
        more.style.display = shown < this.total ? '' : 'none';
        const label = more.querySelector('[data-rbs-more-count]');
        if (label) label.textContent = `${shown} / ${this.total}`;
      }
    }

    renderFacets() {
      const facetsEl = this.querySelector('[data-rbs-facets]');
      if (!facetsEl) return;

      const group = (title, type, items, selected, labelKey, countKey) => {
        if (!items || !items.length) return '';
        return `
          <div class="rbs-facet">
            <div class="rbs-block-heading" data-item="block-heading">${esc(title)}</div>
            <ul class="rbs-facet-list">
              ${items.slice(0, 12).map((it) => {
                const value = String(it[labelKey] || '').trim();
                const checked = selected.includes(value);
                return `
                  <li>
                    <label class="rbs-facet-option${checked ? ' rbs-facet-option--on' : ''}">
                      <input type="checkbox" data-rbs-filter data-filter-type="${esc(type)}"
                        value="${esc(value)}" ${checked ? 'checked' : ''}>
                      <span class="rbs-facet-label">${esc(type === 'categories' ? value.toLowerCase() : value)}</span>
                      <span class="rbs-facet-count">${esc(it[countKey] || '')}</span>
                    </label>
                  </li>`;
              }).join('')}
            </ul>
          </div>`;
      };

      facetsEl.innerHTML = `
        ${group(this.cfg.t.category, 'categories', this.aggregates.categories, this.state.categories, 'categories', 'numProducts')}
        <div class="rbs-facet">
          <div class="rbs-block-heading" data-item="block-heading">${esc(this.cfg.t.price)}</div>
          <form class="rbs-price-form" data-rbs-price-form>
            <input type="number" name="min" min="0" placeholder="${esc(this.cfg.t.min)}" value="${this.state.min ?? ''}" data-item="paragraph">
            <span>&ndash;</span>
            <input type="number" name="max" min="0" placeholder="${esc(this.cfg.t.max)}" value="${this.state.max ?? ''}" data-item="paragraph">
            <button type="submit" data-item="block-link"><div>${esc(this.cfg.t.apply)}</div></button>
          </form>
        </div>
        <div class="rbs-facet">
          <label class="rbs-facet-option">
            <input type="checkbox" data-rbs-filter data-filter-type="stock" ${this.state.inStock ? 'checked' : ''}>
            <span class="rbs-facet-label">${esc(this.cfg.t.inStock)}</span>
          </label>
        </div>`;

      // The price form is re-rendered each pass; rebind its submit.
      facetsEl.querySelector('[data-rbs-price-form]')?.addEventListener('submit', (e) => {
        e.preventDefault();
        const min = parseFloat(e.target.querySelector('[name="min"]').value);
        const max = parseFloat(e.target.querySelector('[name="max"]').value);
        this.state.min = isNaN(min) ? null : min;
        this.state.max = isNaN(max) ? null : max;
        this.state.page = 1;
        this.runPageSearch();
      });
    }

    renderPageError() {
      const grid = this.querySelector('[data-rbs-grid]');
      if (grid) {
        grid.innerHTML = `<div class="rbs-no-results" style="grid-column: 1/-1;">
          <p data-item="paragraph">${esc(this.cfg.t.error)}</p>
        </div>`;
      }
    }
  }

  customElements.define('rebuy-search-element', RebuySearch);
})();
